// Bespoke voxel prefabs (line troops). Split out of creatures_extra.ts, whose
// CREATURE_PREFABS_EXTRA registry still assembles them.

import { HAIR_DARK, SKIN, STEEL, TEAM_DARK, TEAM_LIGHT, box } from './creatures_extra_shared.js';
import type { CharacterPrefab } from './types.js';

// Line infantry: the default "warrior" (previously a placeholder orc).
// Kettle helm, mail over a team tabard, round team shield, spear.
export const soldier: CharacterPrefab = {
  name: 'Soldier',
  description: 'Spearman with kettle helm, team tabard and round shield',
  maxHp: 20,
  parts: [
    {
      name: 'boots',
      voxels: [...box(-2, 0, -1, 2, 1, 3, '#3a2a1c'), ...box(1, 0, -1, 2, 1, 3, '#3a2a1c')],
    },
    {
      name: 'legs',
      voxels: [...box(-2, 1, 0, 2, 4, 2, '#5a4632'), ...box(1, 1, 0, 2, 4, 2, '#5a4632')],
    },
    { name: 'tabard_skirt', voxels: box(-2, 5, -1, 5, 1, 3, TEAM_DARK, { team: true }) },
    {
      name: 'belt',
      voxels: [
        ...box(-2, 6, -1, 5, 1, 3, '#4a3322'),
        { x: 0, y: 6, z: 2, color: '#c9a24a', ...STEEL },
      ],
    },
    {
      name: 'tabard',
      voxels: [
        ...box(-2, 7, -1, 5, 3, 3, TEAM_LIGHT, { team: true }),
        // Darker centre stripe reads as a heraldic charge at 1/16 scale.
        ...box(0, 7, 2, 1, 3, 1, TEAM_DARK, { team: true }),
      ],
    },
    { name: 'mail_shoulders', voxels: box(-3, 10, -1, 7, 1, 3, '#8a8f96', STEEL) },
    {
      name: 'arms',
      voxels: [
        ...box(-3, 7, 0, 1, 3, 1, '#8a8f96', STEEL),
        ...box(3, 7, 0, 1, 3, 1, '#8a8f96', STEEL),
        { x: -3, y: 6, z: 0, color: SKIN },
        { x: 3, y: 6, z: 0, color: SKIN },
      ],
    },
    {
      name: 'head',
      voxels: [
        ...box(-1, 11, -1, 3, 3, 3, SKIN),
        { x: -1, y: 12, z: 2, color: '#2a1e14' },
        { x: 1, y: 12, z: 2, color: '#2a1e14' },
      ],
    },
    {
      name: 'helmet',
      voxels: [
        ...box(-2, 14, -2, 5, 1, 5, '#9aa0a8', STEEL),
        ...box(-1, 15, -1, 3, 1, 3, '#aab0b8', STEEL),
        { x: 0, y: 13, z: 2, color: '#9aa0a8', ...STEEL }, // nasal guard
      ],
    },
    {
      name: 'shield',
      voxels: [
        ...box(-5, 5, 1, 3, 1, 1, '#8a8f96', STEEL),
        ...box(-5, 9, 1, 3, 1, 1, '#8a8f96', STEEL),
        ...box(-5, 6, 1, 3, 3, 1, TEAM_LIGHT, { team: true }),
        { x: -4, y: 7, z: 2, color: '#b0b5bc', ...STEEL }, // boss
      ],
    },
    {
      name: 'spear',
      voxels: [...box(4, 2, 0, 1, 14, 1, '#7a5a3a'), ...box(4, 16, 0, 1, 2, 1, '#c8ccd2', STEEL)],
    },
  ],
};

// Bowman — the default "archer" (previously a skeleton archer placeholder).
export const bowman: CharacterPrefab = {
  name: 'Bowman',
  description: 'Hooded archer with longbow, quiver and team tunic',
  maxHp: 14,
  parts: [
    {
      name: 'boots',
      voxels: [...box(-2, 0, -1, 2, 1, 2, '#3a2a1c'), ...box(1, 0, -1, 2, 1, 2, '#3a2a1c')],
    },
    {
      name: 'legs',
      voxels: [...box(-2, 1, 0, 2, 5, 1, '#6a5a3e'), ...box(1, 1, 0, 2, 5, 1, '#6a5a3e')],
    },
    { name: 'belt', voxels: box(-2, 6, -1, 5, 1, 2, '#4a3322') },
    {
      name: 'tunic',
      voxels: [
        ...box(-2, 7, -1, 5, 4, 2, TEAM_LIGHT, { team: true }),
        ...box(-2, 5, 0, 5, 1, 1, TEAM_DARK, { team: true }),
      ],
    },
    {
      name: 'arms',
      voxels: [
        ...box(-3, 7, 0, 1, 3, 1, TEAM_DARK, { team: true }),
        ...box(3, 7, 0, 1, 3, 1, TEAM_DARK, { team: true }),
      ],
    },
    {
      name: 'head',
      voxels: [
        ...box(-1, 11, -1, 3, 3, 2, SKIN),
        { x: -1, y: 12, z: 1, color: '#2a1e14' },
        { x: 1, y: 12, z: 1, color: '#2a1e14' },
      ],
    },
    {
      name: 'hood',
      voxels: [
        ...box(-2, 14, -2, 5, 1, 4, '#4f5a3a'),
        ...box(-2, 11, -2, 1, 3, 3, '#4f5a3a'),
        ...box(2, 11, -2, 1, 3, 3, '#4f5a3a'),
        ...box(-1, 15, -1, 3, 1, 2, '#4f5a3a'),
      ],
    },
    {
      name: 'bow',
      voxels: [
        { x: -4, y: 5, z: 0, color: '#8b5a2b' },
        ...box(-5, 6, 0, 1, 2, 1, '#8b5a2b'),
        ...box(-6, 8, 0, 1, 4, 1, '#8b5a2b'),
        ...box(-5, 12, 0, 1, 2, 1, '#8b5a2b'),
        { x: -4, y: 14, z: 0, color: '#8b5a2b' },
        ...box(-4, 6, 0, 1, 8, 1, '#e8e0c8'), // string
      ],
    },
    {
      name: 'quiver',
      voxels: [
        ...box(1, 8, -2, 1, 4, 1, '#6b4a2a'),
        ...box(1, 12, -2, 1, 2, 1, '#e8e4dc'), // fletching
      ],
    },
  ],
};

// Knight — the default "cavalry" (previously an orc wolf rider). Modelled in
// side profile along X so the horse reads under the camera-facing billboard.
export const knight: CharacterPrefab = {
  name: 'Knight',
  description: 'Mounted knight with lance, pennant and team caparison',
  maxHp: 30,
  skeletonType: 'quadruped',
  parts: [
    {
      name: 'horse_legs',
      voxels: [
        ...box(-5, 0, -1, 1, 5, 1, '#5a3a22'),
        ...box(-5, 0, 1, 1, 5, 1, '#5a3a22'),
        ...box(3, 0, -1, 1, 5, 1, '#5a3a22'),
        ...box(3, 0, 1, 1, 5, 1, '#5a3a22'),
      ],
    },
    { name: 'horse_body', voxels: box(-5, 5, -1, 10, 3, 3, '#7a5230') },
    {
      name: 'horse_neck_head',
      voxels: [
        ...box(4, 7, -1, 2, 3, 3, '#7a5230'),
        ...box(5, 10, -1, 3, 2, 3, '#7a5230'),
        { x: 8, y: 10, z: 0, color: '#5a3a22' }, // muzzle
        ...box(4, 10, 0, 1, 2, 1, '#2e2016'), // mane
        { x: 6, y: 11, z: 2, color: '#1a1410' }, // eye
      ],
    },
    { name: 'horse_tail', voxels: box(-6, 5, 0, 1, 3, 1, '#2e2016') },
    {
      name: 'caparison',
      voxels: [
        ...box(-4, 4, 2, 8, 4, 1, TEAM_LIGHT, { team: true }),
        ...box(-4, 4, -2, 8, 4, 1, TEAM_LIGHT, { team: true }),
        ...box(-4, 3, 2, 8, 1, 1, TEAM_DARK, { team: true }), // hem
      ],
    },
    {
      name: 'rider_legs',
      voxels: [
        ...box(-1, 7, 2, 2, 2, 1, '#8a8f96', STEEL),
        ...box(-1, 7, -2, 2, 2, 1, '#8a8f96', STEEL),
      ],
    },
    { name: 'rider_torso', voxels: box(-1, 8, -1, 3, 4, 3, TEAM_LIGHT, { team: true }) },
    { name: 'rider_arms', voxels: box(2, 9, 1, 1, 3, 1, '#8a8f96', STEEL) },
    {
      name: 'great_helm',
      voxels: [
        ...box(-1, 12, -1, 3, 3, 3, '#a4aab2', STEEL),
        ...box(0, 13, 2, 2, 1, 1, '#1a1a1a'), // visor slit
        { x: 0, y: 15, z: 0, color: TEAM_LIGHT, team: true }, // crest
      ],
    },
    {
      name: 'lance',
      voxels: [
        ...box(2, 10, 2, 8, 1, 1, '#7a5a3a'),
        ...box(10, 10, 2, 2, 1, 1, '#c8ccd2', STEEL),
        ...box(7, 11, 2, 2, 1, 1, TEAM_DARK, { team: true }), // pennant
      ],
    },
  ],
};

// Catapult — the default "siege" unit (previously an ogre).
export const catapult: CharacterPrefab = {
  name: 'Catapult',
  description: 'Wheeled torsion catapult with loaded stone and team banner',
  maxHp: 30,
  parts: [
    {
      name: 'wheels',
      voxels: [
        ...box(-5, 0, -3, 2, 2, 1, '#3e2c1c'),
        ...box(-5, 0, 3, 2, 2, 1, '#3e2c1c'),
        ...box(3, 0, -3, 2, 2, 1, '#3e2c1c'),
        ...box(3, 0, 3, 2, 2, 1, '#3e2c1c'),
      ],
    },
    { name: 'frame', voxels: box(-5, 2, -2, 10, 1, 5, '#8a6440') },
    {
      name: 'uprights',
      voxels: [
        ...box(0, 3, -2, 1, 5, 1, '#7a5636'),
        ...box(0, 3, 2, 1, 5, 1, '#7a5636'),
        ...box(0, 8, -2, 1, 1, 5, '#6a4a2e'),
      ],
    },
    {
      name: 'arm',
      voxels: [
        { x: -4, y: 3, z: 0, color: '#9a7248' },
        { x: -3, y: 4, z: 0, color: '#9a7248' },
        { x: -2, y: 5, z: 0, color: '#9a7248' },
        { x: -1, y: 6, z: 0, color: '#9a7248' },
        { x: 1, y: 7, z: 0, color: '#9a7248' },
        { x: 2, y: 8, z: 0, color: '#9a7248' },
        { x: 3, y: 9, z: 0, color: '#9a7248' },
      ],
    },
    {
      name: 'bucket',
      voxels: [...box(4, 9, -1, 2, 1, 3, '#5a3e26'), ...box(4, 10, 0, 2, 1, 1, '#8d8a82')],
    },
    {
      name: 'banner',
      voxels: [
        ...box(-5, 3, 0, 1, 9, 1, '#6a4a2e'),
        ...box(-4, 9, 0, 3, 3, 1, TEAM_LIGHT, { team: true }),
        { x: -3, y: 10, z: 1, color: TEAM_DARK, team: true },
      ],
    },
  ],
};

// Cleric — the default "healer" (previously a wizard).
export const cleric: CharacterPrefab = {
  name: 'Cleric',
  description: 'White-robed healer with team sash and glowing staff',
  maxHp: 12,
  parts: [
    {
      name: 'robe',
      voxels: [
        ...box(-2, 0, -1, 5, 6, 3, '#e6e1d3'),
        ...box(-2, 6, -1, 5, 5, 2, '#eeeae0'),
        ...box(-2, 0, 2, 5, 1, 1, TEAM_DARK, { team: true }), // hem
      ],
    },
    {
      name: 'sash',
      voxels: [
        ...box(-2, 6, 1, 5, 1, 1, TEAM_LIGHT, { team: true }),
        ...box(0, 7, 1, 1, 4, 1, TEAM_LIGHT, { team: true }), // stole
      ],
    },
    {
      name: 'arms',
      voxels: [...box(-3, 7, 0, 1, 3, 1, '#e6e1d3'), ...box(3, 7, 0, 1, 3, 1, '#e6e1d3')],
    },
    {
      name: 'head',
      voxels: [
        ...box(-1, 11, -1, 3, 3, 2, SKIN),
        { x: -1, y: 12, z: 1, color: '#2a1e14' },
        { x: 1, y: 12, z: 1, color: '#2a1e14' },
        ...box(-1, 14, -1, 3, 1, 2, HAIR_DARK),
      ],
    },
    {
      name: 'staff',
      voxels: [
        ...box(4, 0, 0, 1, 15, 1, '#8b6a3a'),
        ...box(3, 15, 0, 3, 1, 1, '#d8b44a', { metalness: 0.8, roughness: 0.3 }),
        { x: 4, y: 16, z: 0, color: '#fff4c8', emissive: '#ffe08a' },
      ],
    },
  ],
};

export const guard: CharacterPrefab = {
  name: 'Town Guard',
  description: 'Watchman in chainmail with spear',
  maxHp: 25,
  parts: [
    { name: 'head', voxels: box(-1, 11, -1, 3, 3, 3, SKIN) },
    {
      name: 'helmet',
      voxels: [...box(-2, 13, -2, 5, 2, 5, '#9a9a9a'), ...box(-1, 15, -1, 3, 1, 3, '#9a9a9a')],
    },
    {
      name: 'torso',
      voxels: [
        ...box(-2, 6, -1, 5, 5, 3, '#7a7a7a'),
        // Team tabard over the mail (front face).
        ...box(-1, 6, 2, 3, 4, 1, TEAM_LIGHT, { team: true }),
        ...box(-1, 11, 2, 1, 1, 1, '#5a5a5a'),
        ...box(1, 11, 2, 1, 1, 1, '#5a5a5a'),
      ],
    },
    { name: 'left_arm', voxels: box(-3, 6, 0, 1, 5, 1, '#7a7a7a') },
    { name: 'right_arm', voxels: box(3, 6, 0, 1, 5, 1, '#7a7a7a') },
    {
      name: 'legs',
      voxels: [...box(-2, 0, 0, 2, 6, 1, '#4a4a4a'), ...box(1, 0, 0, 2, 6, 1, '#4a4a4a')],
    },
    {
      name: 'spear',
      voxels: [...box(-3, 0, 0, 1, 13, 1, '#5a3a1a'), ...box(-3, 13, -1, 1, 3, 3, '#cccccc')],
    },
  ],
};

export const paladin: CharacterPrefab = {
  name: 'Paladin',
  description: 'Holy warrior in shining plate with sword',
  maxHp: 40,
  parts: [
    { name: 'head', voxels: box(-1, 11, -1, 3, 3, 3, SKIN) },
    {
      name: 'helm_gilded',
      voxels: [
        ...box(-2, 13, -2, 5, 2, 5, '#e0e0e0'),
        ...box(-1, 15, -1, 3, 1, 3, '#e0e0e0'),
        ...box(-2, 14, 2, 1, 1, 1, '#d4af37'),
        ...box(2, 14, 2, 1, 1, 1, '#d4af37'),
      ],
    },
    {
      name: 'torso_plate',
      voxels: [
        ...box(-2, 6, -1, 5, 5, 3, '#e8e8e8'),
        { x: 0, y: 8, z: 1, color: '#d4af37' },
        { x: 0, y: 7, z: 1, color: '#d4af37' },
      ],
    },
    {
      name: 'cape',
      voxels: [
        { x: -2, y: 5, z: -1, color: '#8b1a1a' },
        { x: -2, y: 4, z: -1, color: '#8b1a1a' },
        { x: -2, y: 3, z: -1, color: '#8b1a1a' },
        { x: 2, y: 5, z: -1, color: '#8b1a1a' },
        { x: 2, y: 4, z: -1, color: '#8b1a1a' },
        { x: 2, y: 3, z: -1, color: '#8b1a1a' },
      ],
    },
    { name: 'left_arm', voxels: box(-3, 6, 0, 1, 5, 1, '#e8e8e8') },
    {
      name: 'right_arm_sword',
      voxels: [
        ...box(3, 6, 0, 1, 3, 1, '#e8e8e8'),
        ...box(3, 9, 0, 1, 1, 1, '#d4af37'),
        ...box(3, 10, 0, 1, 6, 1, '#c8c8d8'),
      ],
    },
    {
      name: 'legs',
      voxels: [...box(-2, 0, 0, 2, 6, 1, '#c8c8c8'), ...box(1, 0, 0, 2, 6, 1, '#c8c8c8')],
    },
  ],
};
